import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import toast from "react-hot-toast";
import EmptyState from "./components/EmptyState";
import RevenueItemsTable from "./components/RevenueItemsTable";
import CalculatedValuesFooter from "./components/CalculatedValuesFooter";
import CurrencyDropdown from "./components/CurrencyDropdown";
import {
  useContract,
  useUpdateContract,
  useUpdateDealCurrency
} from "@/pages/contracts/hooks";
import { formatDateShort } from "./consts";
import { computeContractDateRange } from "./utils";
import Text from "@/components/ui/text";
import { DealData } from "@/pages/deals/types";
import { ServiceItem } from "@/pages/settings/pages/products-v2/types";

interface Props {
  contractId?: string;
  productId?: string;
  dealId?: string;
  companyId?: string;
  onDealUpdate?: (updatedFields: Partial<DealData>) => void;
  confirmServiceChanges?: boolean;
  showCommittedValues?: boolean;
  showStatusColumn?: boolean;
  financialsLocked?: boolean;
  // Rendered beside the currency dropdown; the deal page passes nothing.
  headerAction?: React.ReactNode;
  hideServiceRowActions?: boolean;
  hideAddService?: boolean;
  // Add service also offers the product's bundles; only the deal's page does.
  offerBundles?: boolean;
  // Groups lines under their bundles without offering them (the contract page).
  groupBundles?: boolean;
}

const ContractTermsV2: React.FC<Props> = ({
  contractId,
  productId,
  dealId,
  companyId,
  onDealUpdate,
  confirmServiceChanges = false,
  showCommittedValues = true,
  showStatusColumn = true,
  financialsLocked = false,
  headerAction,
  hideServiceRowActions = false,
  hideAddService = false,
  offerBundles = false,
  groupBundles = false
}) => {
  const { t } = useTranslation();
  const { data: rawContract, isLoading, error } = useContract(contractId);

  // A deal can carry a contractId whose contract has since been deleted. When
  // the fetch confirms a 404, treat the contract as gone everywhere: drop the
  // id from the add-service flow (so the first add creates a fresh contract
  // instead of POSTing revenue items at one that's gone) AND ignore any stale
  // contract body TanStack Query still holds from an earlier successful fetch,
  // so we don't render services/currency/totals for a contract that no longer
  // exists. A transient error (network/5xx) is NOT treated as gone: we keep the
  // id and any retained data rather than spawn a duplicate contract or blank the
  // UI on a fetch that may recover.
  const contractConfirmedGone =
    isAxiosError(error) && error.response?.status === 404;
  const contract = contractConfirmedGone ? undefined : rawContract;
  const liveContractId = contractConfirmedGone ? undefined : contractId;

  // updateContract/updateDealCurrency intentionally use the raw `contractId`
  // (not `liveContractId` above): they drive currency edits via
  // handleCurrencyChange, a separate path from the add-service flow that the
  // dead-contract gate targets.
  const updateContract = useUpdateContract(contractId);
  const updateDealCurrency = useUpdateDealCurrency(contractId);
  const queryClient = useQueryClient();

  // Held here, not in EmptyState, which remounts while a recovered contract loads.
  const [recoveredPick, setRecoveredPick] = useState<ServiceItem | null>(null);
  const clearRecoveredPick = useCallback(() => setRecoveredPick(null), []);

  const handleCreateFailed = useCallback(
    async (service: ServiceItem) => {
      if (dealId) {
        const dealKey = ["deals", { pathParams: { dealsIds: dealId } }];
        // Joins the re-read useCreateContract already started on failure.
        await queryClient.refetchQueries(
          { queryKey: dealKey },
          { cancelRefetch: false }
        );
        // Only a contract other than the one already held (possibly deleted) means the create landed.
        const linked = queryClient
          .getQueriesData<{ deals?: DealData[] }>({ queryKey: dealKey })
          .some(([, data]) =>
            data?.deals?.some(
              (deal) =>
                deal.id === dealId &&
                !!deal.contractId &&
                deal.contractId !== contractId
            )
          );
        if (linked) {
          setRecoveredPick(service);
          return;
        }
      }
      toast.error(t("deals.commercials.updateError"));
    },
    [dealId, contractId, queryClient, t]
  );

  const revenueItems = useMemo(
    () => contract?.revenueItems ?? [],
    [contract?.revenueItems]
  );
  const hasRevenueItems = revenueItems.length > 0;
  // For display only, and now only the dropdown uses it. Everything that either
  // decides whether to convert, or forwards the value to something that does,
  // takes `contract?.currency` raw — including EmptyState, which looks like a
  // display surface but hands the value to AddServiceDrawer.
  const currency = contract?.currency ?? "USD";

  const contractDateRange = useMemo(
    () =>
      computeContractDateRange(
        revenueItems,
        contract?.startDate,
        contract?.endDate
      ),
    [revenueItems, contract?.startDate, contract?.endDate]
  );

  const handleCurrencyChange = useCallback(
    (newCurrency: string) => {
      if (financialsLocked) return;
      if (dealId && !contract?.isDetachedFromDeal) {
        updateDealCurrency.mutate(
          {
            pathParams: { dealId },
            data: { currency: newCurrency }
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ["deals", { pathParams: { dealsIds: dealId } }]
              });
              onDealUpdate?.({ currency: newCurrency });
            }
          }
        );
      } else if (contractId) {
        updateContract.mutate({
          pathParams: { contractId },
          data: { currency: newCurrency }
        });
      }
    },
    [
      contractId,
      dealId,
      financialsLocked,
      updateContract,
      updateDealCurrency,
      onDealUpdate,
      queryClient,
      contract?.isDetachedFromDeal
    ]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    // Scopes the table box's z-10 here, so it outranks only the footer tucked
    // under it and not the record header pinned above the tab.
    <div className="isolate flex flex-col items-center">
      {hasRevenueItems ? (
        <>
          <div className="flex items-center justify-between w-full mb-4 px-2">
            <div className="flex items-center gap-4">
              {contractDateRange && (
                <Text variant="body">
                  {formatDateShort(contractDateRange.start)} -{" "}
                  {formatDateShort(contractDateRange.end)}
                </Text>
              )}
              {contract?.nextRenewal && (
                <Text variant="body">
                  {t("companies.commercials.renewOn", {
                    date: formatDateShort(contract.nextRenewal)
                  })}
                </Text>
              )}
              <Text variant="body">
                {t("contractTerms.serviceCount", {
                  count: revenueItems.length
                })}
              </Text>
            </div>
            <div className="flex items-center gap-2">
              <CurrencyDropdown
                value={currency}
                onChange={handleCurrencyChange}
                disabled={financialsLocked}
                disabledTooltipText={t("common.noEditPermission")}
              />
              {headerAction}
            </div>
          </div>
          <div className="relative z-10 flex flex-col w-full bg-surface-elevation1 rounded-xl border border-border-border1 overflow-hidden">
            <RevenueItemsTable
              revenueItems={revenueItems}
              productId={productId}
              contractId={liveContractId}
              currency={contract?.currency}
              confirmServiceChanges={confirmServiceChanges}
              showStatusColumn={showStatusColumn}
              disabled={financialsLocked}
              disabledTooltipText={t("common.noEditPermission")}
              hideRowActions={hideServiceRowActions}
              hideAddService={hideAddService}
              offerBundles={offerBundles}
              groupBundles={groupBundles}
            />
          </div>
        </>
      ) : (
        <>
          {/* The action self-gates on canUpdateTerms, so a contract with no
              services yet must still be offered it rather than hidden from it. */}
          {headerAction ? (
            <div className="flex items-center justify-end w-full mb-4 px-2">
              {headerAction}
            </div>
          ) : null}
          <div className="relative z-10 flex flex-col w-full bg-surface-elevation1 rounded-xl border border-border-border1 overflow-hidden">
            <EmptyState
              productId={productId}
              contractId={liveContractId}
              currency={contract?.currency}
              dealId={dealId}
              companyId={companyId}
              onDealUpdate={onDealUpdate}
              disabled={financialsLocked}
              disabledTooltipText={t("common.noEditPermission")}
              onCreateFailed={handleCreateFailed}
              pendingService={recoveredPick}
              onPendingServiceConsumed={clearRecoveredPick}
              hideAddService={hideAddService}
              offerBundles={offerBundles}
            />
          </div>
        </>
      )}
      <CalculatedValuesFooter
        mrrLocal={contract?.calculatedMrrLocal ?? null}
        mrr={contract?.calculatedMrr ?? null}
        arrLocal={contract?.calculatedArrLocal ?? null}
        arr={contract?.calculatedArr ?? null}
        cmrrLocal={contract?.calculatedCmrrLocal ?? null}
        cmrr={contract?.calculatedCmrr ?? null}
        carrLocal={contract?.calculatedCarrLocal ?? null}
        carr={contract?.calculatedCarr ?? null}
        acvLocal={contract?.calculatedAnnualContractValueLocal ?? null}
        acv={contract?.calculatedAnnualContractValue ?? null}
        tcvLocal={contract?.calculatedTotalContractValueLocal ?? null}
        tcv={contract?.calculatedTotalContractValue ?? null}
        currency={contract?.currency}
        showCommitted={showCommittedValues}
      />
    </div>
  );
};

export default ContractTermsV2;
